<template>
  <div class="location-highlights-table">
    <div class="location-highlights-table__heading">
      <h2 class="global-h4 location-highlights-table__title">Promotion by location</h2>

      <div class="location-highlights-table__controls">
        <SrpSegmentedControl
          :modelValue="props.activeTab"
          :options="tabOptions"
          size="sm"
          ariaLabel="Filter locations"
          @update:modelValue="value => emit('update:activeTab', value as ActiveTab)"
        />
        <input class="global-text-input location-highlights-table__search" type="text" placeholder="Search locations" v-model="search" />
      </div>
    </div>

    <table class="location-highlights-table__table">
      <thead>
        <tr class="location-highlights-table__header-row">
          <th class="location-highlights-table__th">Location</th>
          <th class="location-highlights-table__th location-highlights-table__th--number">Posts</th>
          <th class="location-highlights-table__th location-highlights-table__th--number">Highlights</th>
          <th class="location-highlights-table__th location-highlights-table__th--date">Last highlighted</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!visibleRows.length">
          <td class="location-highlights-table__empty-cell" colspan="4">No locations match</td>
        </tr>
        <tr v-for="row in visibleRows" :key="row.locationId" class="location-highlights-table__row">
          <td class="location-highlights-table__td">
            <div class="location-highlights-table__location">
              <LocationAvatar :src="row.icon" :name="row.name" :size="32" />
              <span class="location-highlights-table__name">{{ row.name }}</span>
            </div>
          </td>
          <td class="location-highlights-table__td location-highlights-table__td--number">{{ row.postCount }}</td>
          <td class="location-highlights-table__td location-highlights-table__td--number">{{ row.highlightCount }}</td>
          <td class="location-highlights-table__td location-highlights-table__td--date">{{ lastHighlightedText(row) }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import DayjsExtended from "@logic/DayjsExtended";

// Components
import LocationAvatar from "@components/SocialLocations/LocationAvatar.vue";
import SrpSegmentedControl from "@components/ui/SrpSegmentedControl.vue";

// Types
import type { LocationHighlightRow } from "@contracts/socialReports";

type ActiveTab = "highlighted" | "notYet" | "all";

const props = defineProps<{
  rows: LocationHighlightRow[];
  activeTab: ActiveTab;
}>();

const emit = defineEmits<{
  (e: "update:activeTab", value: ActiveTab): void;
}>();

const search = ref<string>("");

function clearSearch() {
  search.value = "";
}

defineExpose({ clearSearch });

// Tabs =======================================================================
const highlightedRows = computed<LocationHighlightRow[]>(() => props.rows.filter(r => r.postCount > 0));
const notYetRows = computed<LocationHighlightRow[]>(() => props.rows.filter(r => r.postCount === 0));

const tabOptions = computed(() => [
  { value: "highlighted", label: `Highlighted (${highlightedRows.value.length})` },
  { value: "notYet", label: `Not yet highlighted (${notYetRows.value.length})` },
  { value: "all", label: `All (${props.rows.length})` },
]);

const tabRows = computed<LocationHighlightRow[]>(() => {
  if (props.activeTab === "highlighted") return highlightedRows.value;
  if (props.activeTab === "notYet") return notYetRows.value;
  return props.rows;
});

const visibleRows = computed<LocationHighlightRow[]>(() => {
  const query = search.value.trim().toLowerCase();
  if (!query) return tabRows.value;
  return tabRows.value.filter(r => (r.name ?? "").toLowerCase().includes(query));
});

// Last highlighted ===========================================================
function lastHighlightedText(row: LocationHighlightRow): string {
  if (!row.lastHighlightedAt) return "—";
  const date = DayjsExtended(row.lastHighlightedAt);
  if (!date.isValid()) return "—";
  if (date.isAfter(DayjsExtended())) return `Scheduled ${date.format("MMM D")}`;
  return date.format("MMM D, YYYY");
}
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

// Location highlights table ==================================================
.location-highlights-table {
  box-sizing: border-box;
  padding: 22px 24px;
  border: 1px solid #e4e7ec;
  border-radius: 11px;
  background: #fff;

  &__heading {
    margin-bottom: 16px;
    gap: 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
  }

  &__title {
    margin: 0;
  }

  &__controls {
    gap: 12px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  &__search {
    width: 200px;
    height: 32px;
    padding: 4px 10px;
    font-size: 14px;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
  }

  &__header-row {
    border-bottom: 1px solid #f0f2f5;
  }

  &__th {
    padding: 0 10px 10px 0;
    color: #667085;
    font: 500 14px/18px sans-serif;
    text-align: left;
    white-space: nowrap;

    &--number {
      width: 90px;
      text-align: right;
    }

    &--date {
      width: 150px;
      text-align: right;
    }
  }

  &__row {
    border-bottom: 1px solid #f0f2f5;

    &:last-child {
      border-bottom: 0;
    }
  }

  &__td {
    padding: 10px 10px 10px 0;
    color: #102f41;
    font: 400 14px/18px sans-serif;
    vertical-align: middle;

    &--number {
      font-family: $font-family;
      font-weight: 600;
      text-align: right;
    }

    &--date {
      color: #667085;
      text-align: right;
      white-space: nowrap;
    }
  }

  &__location {
    gap: 11px;
    display: flex;
    align-items: center;
  }

  &__name {
    min-width: 0;
    font-weight: 500;
    overflow-wrap: anywhere;
  }

  &__empty-cell {
    padding: 18px 0;
    color: #667085;
    font: 400 14px/18px sans-serif;
  }
}

// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .location-highlights-table {
    padding: 18px 15px;

    &__search {
      width: 100%;
    }

    &__th--number,
    &__td--number {
      width: 62px;
    }

    &__th--date,
    &__td--date {
      width: 108px;
      white-space: normal;
    }
  }
}
</style>
